/*
 * Fichaje page — control horario del operario.
 *
 * Flujo:
 *  - Muestra estado actual (fuera / trabajando / pausa) y horas del día.
 *  - Botones grandes: Entrada, Pausa, Reanudar, Salida.
 *  - Al fichar pide geolocalización (opcional) y la envía al backend.
 *  - Modo "seguimiento GPS": si el operario da permiso, la app watcha su
 *    posición y crea automáticamente visita_site_in/out cuando entra/sale
 *    de un radio de 150 m alrededor de un site.
 *  - Al final del día: firma en canvas y sella el parte.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useSyncListener } from '../hooks/useDataSync';
import { Loader2, LogIn, LogOut, Coffee, Play, MapPin, PenLine, RefreshCw, Wifi, WifiOff, Trash2, Signature, ShieldCheck } from 'lucide-react';

const fmtMin = (mins) => {
  if (!Number.isFinite(mins) || mins <= 0) return '0h 00m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
};
const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

// --- Geolocation helper ---
const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  navigator.geolocation.getCurrentPosition(
    (pos) => resolve({
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy_m: Math.round(pos.coords.accuracy || 0),
    }),
    () => resolve(null),
    { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
  );
});

const KIND_LABEL = {
  entrada: 'Entrada',
  salida: 'Salida',
  pausa_inicio: 'Inicio pausa',
  pausa_fin: 'Fin pausa',
  visita_site_in: 'Llegada a site',
  visita_site_out: 'Salida de site',
};

const KIND_COLOR = {
  entrada: 'text-[#A3FF12]',
  salida: 'text-red-400',
  pausa_inicio: 'text-orange-400',
  pausa_fin: 'text-orange-300',
  visita_site_in: 'text-blue-400',
  visita_site_out: 'text-blue-300',
};

// -------- Signature pad --------
const SignaturePad = ({ onSaved, onCancel }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const c = canvasRef.current;
    // scale for retina
    const parent = c.parentElement;
    const w = parent.clientWidth;
    const h = 200;
    c.width = w * window.devicePixelRatio;
    c.height = h * window.devicePixelRatio;
    c.style.width = w + 'px';
    c.style.height = h + 'px';
    const ctx = c.getContext('2d');
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#A3FF12';
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, w, h);
  }, []);

  const pos = (e) => {
    const c = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - c.left, y: t.clientY - c.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, c.width, c.height);
  };

  const save = () => {
    const dataUrl = canvasRef.current.toDataURL('image/png');
    onSaved(dataUrl, notes);
  };

  return (
    <div className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur flex items-center justify-center p-4" data-testid="signature-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-4 sm:p-6 w-full max-w-lg">
        <div className="flex items-center gap-2 mb-3">
          <PenLine className="text-[#A3FF12]" size={20}/>
          <h3 className="text-xl font-black text-white">Firmar parte del día</h3>
        </div>
        <p className="text-white/50 text-sm mb-4">Firma con el dedo o el ratón. Al firmar quedará sellado el total de horas y no podrás editarlas.</p>
        <div className="rounded-xl border border-white/10 overflow-hidden bg-[#0a0a0a]">
          <canvas ref={canvasRef} data-testid="signature-canvas"
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
            className="block w-full touch-none cursor-crosshair"/>
        </div>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Notas del día (opcional)…"
          className="mt-3 w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white/90 min-h-[70px]"/>
        <div className="flex items-center justify-between gap-2 mt-4">
          <button onClick={clear} data-testid="signature-clear" className="px-3 py-2 text-sm text-white/60 hover:text-white flex items-center gap-1"><Trash2 size={14}/> Borrar</button>
          <div className="flex items-center gap-2">
            <button onClick={onCancel} className="px-4 py-2 text-sm text-white/70 hover:text-white">Cancelar</button>
            <button onClick={save} data-testid="signature-save" className="px-4 py-2 bg-[#A3FF12] text-black font-black rounded-xl hover:bg-[#b8ff3a] text-sm">Firmar parte</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Fichaje = () => {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(null); // kind while sending
  const [signOpen, setSignOpen] = useState(false);
  const [gpsFollow, setGpsFollow] = useState(false);
  const watchIdRef = useRef(null);
  const insideRef = useRef({}); // { code: {name, lat, lng} } sites currently inside

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/time-entries/today');
      setState(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useSyncListener(load, ['time_entries', 'workday_signatures']);

  const stamp = async (kind, extras = {}) => {
    if (posting) return;
    setPosting(kind);
    try {
      const pos = await getPosition();
      const payload = { kind, ...(pos || {}), ...extras, client_time: new Date().toISOString() };
      await api.post('/time-entries', payload);
      await load();
    } catch (e) {
      alert('Error al fichar: ' + (e.response?.data?.detail || e.message));
    } finally { setPosting(null); }
  };

  const sign = async (dataUrl, notes) => {
    try {
      await api.post('/time-entries/sign', { signature_data_url: dataUrl, notes });
      setSignOpen(false);
      await load();
    } catch (e) {
      alert('Error al firmar: ' + (e.response?.data?.detail || e.message));
    }
  };

  // ---- Auto-visita por GPS ----
  const nearbyRadiusM = 150;
  const stopGps = useCallback(() => {
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setGpsFollow(false);
    insideRef.current = {};
  }, []);
  const startGps = useCallback(() => {
    if (!navigator.geolocation) {
      alert('Este dispositivo no soporta GPS');
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      async (p) => {
        const lat = p.coords.latitude;
        const lng = p.coords.longitude;
        const accuracy_m = Math.round(p.coords.accuracy || 0);
        try {
          // pide torres cercanas (~250m bbox)
          const delta = 0.003;
          const { data } = await api.get('/towers', {
            params: {
              min_lat: lat - delta, max_lat: lat + delta,
              min_lng: lng - delta, max_lng: lng + delta,
              limit: 15,
            }
          });
          const towers = data || [];
          // Haversine
          const dist = (a, b) => {
            const R = 6371000;
            const p1 = a.lat * Math.PI / 180, p2 = b.lat * Math.PI / 180;
            const dp = (b.lat - a.lat) * Math.PI / 180;
            const dl = (b.lng - a.lng) * Math.PI / 180;
            const x = Math.sin(dp/2)**2 + Math.cos(p1)*Math.cos(p2)*Math.sin(dl/2)**2;
            return 2 * R * Math.asin(Math.sqrt(x));
          };
          const inside = towers.filter((t) => t.lat && t.lng && dist({ lat, lng }, t) <= nearbyRadiusM);
          const insideCodes = new Set(inside.map((t) => t.code));
          // enter events
          for (const t of inside) {
            if (!insideRef.current[t.code]) {
              await api.post('/time-entries', {
                kind: 'visita_site_in', lat, lng, accuracy_m,
                site_id: t.id, site_code: t.code, site_name: t.name || t.code,
                source: 'gps_auto',
              });
              insideRef.current[t.code] = t;
            }
          }
          // leave events
          for (const code of Object.keys(insideRef.current)) {
            if (!insideCodes.has(code)) {
              const prev = insideRef.current[code];
              await api.post('/time-entries', {
                kind: 'visita_site_out', lat, lng, accuracy_m,
                site_code: code, site_name: prev?.name || code,
                source: 'gps_auto',
              });
              delete insideRef.current[code];
            }
          }
        } catch { /* silencio, seguimos con la siguiente posición */ }
      },
      () => { alert('No se pudo activar el GPS. Revisa los permisos.'); stopGps(); },
      { enableHighAccuracy: true, maximumAge: 20000, timeout: 15000 }
    );
    setGpsFollow(true);
  }, [stopGps]);
  useEffect(() => () => stopGps(), [stopGps]);

  if (loading) {
    return <div className="p-8 flex items-center justify-center min-h-[60vh]"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>;
  }

  const status = state?.status || 'fuera';
  const isWorking = status === 'trabajando';
  const isPaused = status === 'pausa';
  const isOut = status === 'fuera';
  const signed = state?.signed;

  const statusMap = {
    fuera: { label: 'Fuera de jornada', color: 'bg-white/5 border-white/10 text-white/60' },
    trabajando: { label: 'Trabajando', color: 'bg-[#A3FF12]/10 border-[#A3FF12]/40 text-[#A3FF12]' },
    pausa: { label: 'En pausa', color: 'bg-orange-500/10 border-orange-500/40 text-orange-300' },
  };
  const statusInfo = statusMap[status] || statusMap.fuera;

  return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto space-y-6" data-testid="fichaje-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold tracking-widest text-[#A3FF12]">CONTROL HORARIO</div>
          <h1 className="text-3xl sm:text-4xl font-black mt-1">Fichaje</h1>
          <p className="text-white/50 text-sm mt-1">Hola {user?.name?.split(' ')[0]}. Registra tu jornada y visitas a sites.</p>
        </div>
        <button onClick={load} className="p-2 text-white/50 hover:text-[#A3FF12]" title="Recargar" data-testid="fichaje-reload">
          <RefreshCw size={18}/>
        </button>
      </div>

      {/* Stat card */}
      <div className="bg-gradient-to-br from-[#0a0a0a] to-black border border-white/10 rounded-3xl p-6 sm:p-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <span className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest border ${statusInfo.color}`} data-testid="fichaje-status-pill">{statusInfo.label}</span>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-white/40">Trabajadas hoy</div>
            <div className="text-4xl sm:text-5xl font-black text-white mt-1" data-testid="fichaje-total">
              {fmtMin(state?.total_minutes)}
            </div>
            <div className="text-white/40 text-xs mt-1">Pausas: {fmtMin(state?.break_minutes)}</div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <button onClick={() => stamp('entrada')} disabled={isWorking || isPaused || signed || posting} data-testid="btn-entrada"
            className="flex flex-col items-center gap-1.5 px-3 py-4 rounded-2xl bg-[#A3FF12] text-black font-black hover:bg-[#b8ff3a] disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            {posting === 'entrada' ? <Loader2 className="animate-spin" size={22}/> : <LogIn size={22}/>}
            <span className="text-sm">Entrada</span>
          </button>
          <button onClick={() => stamp('pausa_inicio')} disabled={!isWorking || signed || posting} data-testid="btn-pausa"
            className="flex flex-col items-center gap-1.5 px-3 py-4 rounded-2xl bg-orange-400/10 border border-orange-400/30 text-orange-300 font-black hover:bg-orange-400/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            {posting === 'pausa_inicio' ? <Loader2 className="animate-spin" size={22}/> : <Coffee size={22}/>}
            <span className="text-sm">Pausa</span>
          </button>
          <button onClick={() => stamp('pausa_fin')} disabled={!isPaused || signed || posting} data-testid="btn-reanudar"
            className="flex flex-col items-center gap-1.5 px-3 py-4 rounded-2xl bg-orange-400/10 border border-orange-400/30 text-orange-300 font-black hover:bg-orange-400/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            {posting === 'pausa_fin' ? <Loader2 className="animate-spin" size={22}/> : <Play size={22}/>}
            <span className="text-sm">Reanudar</span>
          </button>
          <button onClick={() => stamp('salida')} disabled={isOut || signed || posting} data-testid="btn-salida"
            className="flex flex-col items-center gap-1.5 px-3 py-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-300 font-black hover:bg-red-500/20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
            {posting === 'salida' ? <Loader2 className="animate-spin" size={22}/> : <LogOut size={22}/>}
            <span className="text-sm">Salida</span>
          </button>
        </div>

        {/* GPS follow toggle */}
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3 p-3 bg-black/40 border border-white/10 rounded-2xl">
          <div className="flex items-center gap-2 min-w-0">
            {gpsFollow ? <Wifi size={18} className="text-[#A3FF12] flex-shrink-0"/> : <WifiOff size={18} className="text-white/40 flex-shrink-0"/>}
            <div className="min-w-0">
              <div className="text-white font-bold text-sm">Seguimiento GPS de visitas</div>
              <div className="text-white/50 text-xs">Registra entradas/salidas de sites automáticamente (radio 150m).</div>
            </div>
          </div>
          <button onClick={() => (gpsFollow ? stopGps() : startGps())}
            data-testid="btn-gps-follow"
            className={`px-4 py-2 rounded-xl text-sm font-black flex-shrink-0 ${gpsFollow ? 'bg-red-500/10 border border-red-500/30 text-red-300' : 'bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12]'}`}>
            {gpsFollow ? 'Detener GPS' : 'Activar GPS'}
          </button>
        </div>

        {/* Sign / signed banner */}
        <div className="mt-4">
          {signed ? (
            <div className="p-4 bg-[#A3FF12]/5 border border-[#A3FF12]/30 rounded-2xl flex items-center gap-3" data-testid="fichaje-signed-banner">
              <ShieldCheck className="text-[#A3FF12] flex-shrink-0" size={22}/>
              <div className="flex-1">
                <div className="text-white font-black text-sm">Parte firmado</div>
                <div className="text-white/50 text-xs">Sellado el {fmtTime(state?.signature?.signed_at)} · {fmtMin(state?.signature?.total_minutes)}</div>
              </div>
              {state?.signature?.signature_data_url && (
                <img src={state.signature.signature_data_url} alt="firma"
                  className="h-12 bg-black rounded-lg border border-white/10"/>
              )}
            </div>
          ) : (
            <button onClick={() => setSignOpen(true)} disabled={isOut && (state?.entries?.length || 0) === 0}
              data-testid="btn-open-sign"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 text-white font-black disabled:opacity-40 disabled:cursor-not-allowed">
              <Signature size={18}/> Firmar parte del día
            </button>
          )}
        </div>
      </div>

      {/* Entries timeline */}
      <div className="bg-black/40 border border-white/10 rounded-2xl p-4 sm:p-6">
        <h2 className="text-lg font-black mb-4">Movimientos de hoy</h2>
        {(!state?.entries || state.entries.length === 0) ? (
          <div className="text-white/40 text-sm py-6 text-center">Aún no has fichado hoy. Pulsa <b>Entrada</b> para empezar.</div>
        ) : (
          <ul className="space-y-2" data-testid="fichaje-entries">
            {state.entries.slice().reverse().map((e) => (
              <li key={e.id} className="flex items-center gap-3 p-3 bg-black/50 border border-white/5 rounded-xl">
                <div className={`w-2 h-2 rounded-full ${KIND_COLOR[e.kind]?.replace('text-', 'bg-') || 'bg-white/40'}`}/>
                <div className={`font-bold text-sm ${KIND_COLOR[e.kind] || 'text-white'}`}>{KIND_LABEL[e.kind] || e.kind}</div>
                <div className="text-white/60 text-sm">{fmtTime(e.server_time)}</div>
                {e.site_code && (
                  <div className="text-blue-400 text-xs bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <MapPin size={11}/> {e.site_code}
                  </div>
                )}
                <div className="flex-1"/>
                {e.lat && e.lng && (
                  <a href={`https://www.google.com/maps?q=${e.lat},${e.lng}`} target="_blank" rel="noreferrer"
                    className="text-white/40 text-xs hover:text-[#A3FF12] flex items-center gap-1" title={`±${e.accuracy_m || '?'}m`}>
                    <MapPin size={11}/> ubicación
                  </a>
                )}
                {e.source === 'gps_auto' && <span className="text-[10px] text-white/30 uppercase tracking-widest">auto</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {signOpen && <SignaturePad onSaved={sign} onCancel={() => setSignOpen(false)}/>}
    </div>
  );
};

export default Fichaje;
